/**
 * 日期工具方法封装
 * 基于dayjs库实现，提供日期格式化功能
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc'; // 引入 UTC 支持插件，因为时区插件依赖于它

// 加载插件
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.tz.setDefault('America/New_York');

/**
 * 日期格式枚举
 */
export enum DateFormat {
  DATE = 'YYYY-MM-DD',
  DATE_TIME = 'YYYY-MM-DD HH:mm:ss',
  DATE_TIME_MINUTE = 'YYYY-MM-DD HH:mm',
  TIME = 'HH:mm:ss',
  TIME_MINUTE = 'HH:mm',
  MONTH_DAY = 'MM-DD',
  YEAR_MONTH = 'YYYY-MM',
  DATE_SLASH = 'YYYY/MM/DD',
  DATE_TIME_SLASH = 'YYYY/MM/DD HH:mm:ss',
  DATE_DOT = 'YYYY.MM.DD',
  DATE_TIME_DOT = 'YYYY.MM.DD HH:mm:ss',
  // ! 由于站点差异，后端使用的时间格式不一致，前端保持同步
  DATE_UK_FR = 'DD/MM/YYYY',
  DATE_DEFAULT = 'MMM DD, YYYY',
  DATE_DE = 'DD.MM.YYYY',
}

/**
 * 日期工具类
 */
export const DateUtils = {
  /**
   * 获取当前时间
   * @param format 格式化字符串
   * @returns 格式化后的当前时间字符串
   */
  now(format: string = DateFormat.DATE_TIME): string {
    return dayjs().format(format);
  },

  /**
   * 格式化日期
   * @param date 日期对象、时间戳或日期字符串
   * @param format 格式化字符串
   * @returns 格式化后的日期字符串
   */
  format(date: Date | number | string, format: string = DateFormat.DATE_TIME): string {
    return dayjs(date).format(format);
  },

  /**
   * 解析日期字符串
   * @param dateStr 日期字符串
   * @param format 日期格式
   * @returns Dayjs对象
   */
  parse(dateStr: string, format?: string): dayjs.Dayjs {
    return format ? dayjs(dateStr, format) : dayjs(dateStr);
  },

  /**
   * 格式化为日期 (YYYY-MM-DD)
   * @param date 日期对象、时间戳或日期字符串
   * @returns 格式化后的日期字符串
   */
  formatDate(date: Date | number | string): string {
    return this.format(date, DateFormat.DATE);
  },

  /**
   * 格式化为日期时间 (YYYY-MM-DD HH:mm:ss)
   * @param date 日期对象、时间戳或日期字符串
   * @returns 格式化后的日期时间字符串
   */
  formatDateTime(date: Date | number | string): string {
    return this.format(date, DateFormat.DATE_TIME);
  },

  /**
   * 格式化为时间 (HH:mm:ss)
   * @param date 日期对象、时间戳或日期字符串
   * @returns 格式化后的时间字符串
   */
  formatTime(date: Date | number | string): string {
    return this.format(date, DateFormat.TIME);
  },

  /**
   * 格式化为斜杠分隔的日期 (YYYY/MM/DD)
   * @param date 日期对象、时间戳或日期字符串
   * @returns 格式化后的日期字符串
   */
  formatDateSlash(date: Date | number | string): string {
    return this.format(date, DateFormat.DATE_SLASH);
  },

  /**
   * 格式化为点分隔的日期 (YYYY.MM.DD)
   * @param date 日期对象、时间戳或日期字符串
   * @returns 格式化后的日期字符串
   */
  formatDateDot(date: Date | number | string): string {
    return this.format(date, DateFormat.DATE_DOT);
  },

  /**
   * 获取倒计时
   * @param targetDate 目标日期
   * @returns 包含天、时、分、秒的对象
   */
  getCountdown(
    targetDate: Date | number | string,
    inputFormat?: DateFormat,
  ): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } {
    const now = dayjs(dayjs.tz(dayjs(), 'America/New_York').format(DateFormat.DATE_TIME)); // '2013-11-18T11:55:20-05:00'
    const target = dayjs(dayjs(targetDate, inputFormat).format(DateFormat.DATE_TIME));
    const diff = target.diff(now, 'second');

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (24 * 60 * 60));
    const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((diff % (60 * 60)) / 60);
    const seconds = diff % 60;

    return { days, hours, minutes, seconds };
  },

  /**
   * 格式化倒计时
   * @param targetDate 目标日期
   * @param format 格式化字符串，例如 "DD天HH小时mm分钟ss秒"
   * @returns 格式化后的倒计时字符串
   */
  formatCountdown(targetDate: Date | number | string, format = 'DD天HH小时mm分钟ss秒'): string {
    const countdown = this.getCountdown(targetDate);
    let result = format;

    result = result.replace(/DD/g, countdown.days.toString().padStart(2, '0'));
    result = result.replace(/HH/g, countdown.hours.toString().padStart(2, '0'));
    result = result.replace(/mm/g, countdown.minutes.toString().padStart(2, '0'));
    result = result.replace(/ss/g, countdown.seconds.toString().padStart(2, '0'));

    return result;
  },
  // ! 由于站点差异，后端使用的时间格式不一致，前端保持同步
  // ! 如果需要处理后端返回的时间格式，可能会用到该方法，后端的时间差异化函数涉及评论 订单 优惠券  博客 赠品等模块
  getFormatByLocale(locale: string) {
    const formatMap = {
      de: DateFormat.DATE_DE,
      fr: DateFormat.DATE_UK_FR,
      uk: DateFormat.DATE_UK_FR,
    };
    return formatMap[locale] || DateFormat.DATE_DEFAULT;
  },
};

export default DateUtils;
